import "./utils/tinodeGlobalErrorSuppressor";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { ProSidebarProvider } from "react-pro-sidebar";
import GlobalStateProvider from "./components/context/GlobalStateContext";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import store from "./redux/store";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { APIProvider } from "@vis.gl/react-google-maps";
import { NotificationProvider } from "./components/context/NotificationContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1,
    },
  },
});

// Global error handler untuk suppress geolocation errors yang looping
if (typeof window !== "undefined") {
  // Helper function untuk detect geolocation error
  const isGeolocationError = (error) => {
    if (!error) return false;

    // Check if it's a GeolocationPositionError by checking properties
    // GeolocationPositionError has code (1, 2, or 3) and message
    const hasGeolocationCode = error.code && [1, 2, 3].includes(error.code);
    const hasGeolocationMessage =
      error.message &&
      (error.message.includes("Geolocation") ||
        error.message.includes("denied") ||
        error.message.includes("position unavailable") ||
        error.message.includes("timeout"));

    // Check constructor name (safer than instanceof)
    const isGeolocationErrorType =
      error.constructor &&
      error.constructor.name === "GeolocationPositionError";

    return (
      hasGeolocationCode || hasGeolocationMessage || isGeolocationErrorType
    );
  };

  // Suppress unhandled promise rejections dari geolocation
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason && isGeolocationError(event.reason)) {
      // Suppress geolocation errors - jangan tampilkan di console
      event.preventDefault();
      console.warn(
        "Geolocation error suppressed:",
        event.reason.message || "User denied geolocation",
      );
      return;
    }
  });

  // Suppress error events dari geolocation
  window.addEventListener("error", (event) => {
    if (event.error && isGeolocationError(event.error)) {
      // Suppress geolocation errors
      event.preventDefault();
      console.warn(
        "Geolocation error suppressed:",
        event.error.message || "User denied geolocation",
      );
      return;
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <QueryClientProvider client={queryClient}>
          <NotificationProvider>
            <BrowserRouter>
              <ProSidebarProvider>
                <GlobalStateProvider>
                  <APIProvider
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    language="id"
                    region="ID"
                  >
                    <App />
                  </APIProvider>
                </GlobalStateProvider>
              </ProSidebarProvider>
            </BrowserRouter>
          </NotificationProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </Provider>
  </React.StrictMode>,
);
